import React, { useEffect, useState, useRef } from "react";
import {
  Typography,
  Table,
  Input,
  Button,
  Modal,
  Form,
  message,
  Space,
  Spin,
  Select,
  Menu,
  Dropdown,
  InputNumber,
  Checkbox,
} from "antd";
import { ResidueType } from "../../types/residue.type";
import { UnitOfMeasurement } from "../../types/unitOfMeasurement.type";
import { DownOutlined } from "@ant-design/icons";
import { api } from "../../api/api";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/auth.context";
import { canEditMaterials } from "../../utils/roleUtils";
import { Currency } from "../../types/currency.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import { formatNumber } from "../../utils/formatNumber";
import { useQueryParams } from "../../hooks/useQueryParams";
import { useSearchQueryParam } from "../../hooks/useSearchQueryParam";
import HighlightText from "../../components/HighlightText/highlightText.component";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function Materials() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef<any>(null);
  const queryParams = useQueryParams();
  
  const [materials, setMaterials] = useState<ResidueType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  
  // Инициализация состояний из URL query параметров
  const { searchTerm, setSearchTerm, debouncedSearchTerm } = useSearchQueryParam();
  const [unitFilter, setUnitFilter] = useState<string | undefined>(queryParams.getParam("unit") || undefined);
  const [sortBy, setSortBy] = useState<string>(queryParams.getParam("sortBy") || "name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">((queryParams.getParam("sortOrder") as "asc" | "desc") || "asc");
  const [page, setPage] = useState<number>(queryParams.getParamAsNumber("page", 1));
  const [pageSize, setPageSize] = useState<number>(queryParams.getParamAsNumber("pageSize", 10));
  const [total, setTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [hasResidue, setHasResidue] = useState<boolean>(queryParams.getParamAsBoolean("hasResidue"));
  const [belowLimit, setBelowLimit] = useState<boolean>(queryParams.getParamAsBoolean("belowLimit"));
  const [selectedMaterial, setSelectedMaterial] = useState<ResidueType | null>(null);
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [form] = Form.useForm();
  const { exchangeRate } = useExchangeRate();
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);

  // Синхронизация состояний с URL query параметрами
  useEffect(() => {
    const params: Record<string, string | number | boolean | undefined> = {};
    
    if (searchTerm) params.search = searchTerm;
    if (unitFilter) params.unit = unitFilter;
    if (sortBy !== "name") params.sortBy = sortBy;
    if (sortOrder !== "asc") params.sortOrder = sortOrder;
    if (page !== 1) params.page = page;
    if (pageSize !== 10) params.pageSize = pageSize;
    if (hasResidue) params.hasResidue = true;
    if (belowLimit) params.belowLimit = true;
    
    queryParams.setParams(params);
  }, [searchTerm, unitFilter, sortBy, sortOrder, page, pageSize, hasResidue, belowLimit]);

  // Обработка поиска из Command Palette
  useEffect(() => {
    const state = location.state as { searchQuery?: string };
    if (state?.searchQuery) {
      setSearchTerm(state.searchQuery);
      setPage(1); // Сбрасываем страницу на первую
      // Очищаем state после использования
      window.history.replaceState({}, document.title);
      // Устанавливаем фокус на поле поиска
      setTimeout(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.select(); // Выделяем текст для удобства
      }, 200);
    }
  }, [location.pathname]); // Зависимость от pathname, а не всего location

  useEffect(() => {
    fetchMaterials();
  }, [page, debouncedSearchTerm, unitFilter, sortBy, sortOrder, pageSize, hasResidue, belowLimit]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/residue`, {
        params: {
          page,
          type: "materials",
          limit: pageSize,
          search: debouncedSearchTerm,
          sortBy,
          sortOrder,
          hasResidue: hasResidue ? true : undefined,
          belowLimit: belowLimit ? "true" : undefined,
        },
      });
      setMaterials(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching materials:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleAddMaterial = () => {
    form.resetFields();
    setSelectedMaterial(null);
    setIsModalOpen(true);
  };

  const handleEditMaterial = (material: ResidueType) => {
    const data = {
      ...material,
      price: {
        amount: material.price.USD, // Используем USD как основную валюту
        exchangeRate: material.price.exchangeRate,
        currency: material.price.currency,
      },
    };

    
    form.setFieldsValue(data);
    setSelectedMaterial(material);
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (values: any) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
        price: {
          amount: values.price.amount,
          exchangeRate: exchangeRate,
          currency: values.price.currency,
        },
      };

      console.log(data);
      
      if (selectedMaterial) {
        await api.put(`/material/${selectedMaterial._id}`, data);
        message.success("Материал обновлен.");
      } else {
        await api.post(`/material`, data);
        message.success("Материал добавлен.");
      }
      fetchMaterials();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving material:", error);
      message.error(error.response.data.error);
    }
  };

  const handleExport = () => {
    downloadFile("/export/residues", "materials_data.xlsx", { type: "materials" });
  };

  const handlePrintMaterials = () => {
    if (selectedMaterials.length === 0) {
      message.warning("Пожалуйста, выберите хотя бы один материал для печати.");
      return;
    }

    const selectedMaterialsData = materials.filter(material =>
      selectedMaterials.includes(material._id)
    );

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString('ru-RU');

    // Подсчет итогов
    const totalResidue = selectedMaterialsData.reduce((sum, material) => sum + (material.residue || 0), 0);
    const totalResidueKg = selectedMaterialsData.reduce((sum, material) => sum + (material.residueKg || 0), 0);

    const printHtml = `
      <html>
        <head>
          <title>Отчет по материалам</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
              margin: 20px;
              color: #222;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .header h1 {
              margin: 0;
              font-size: 18px;
              font-weight: bold;
            }
            .header .info {
              margin-top: 5px;
              font-size: 12px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              border: 2px solid #000;
            }
            th, td {
              border: 1px solid #000;
              padding: 8px;
              text-align: left;
              font-size: 11px;
            }
            th {
              background-color: #f0f0f0;
              font-weight: bold;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .summary {
              margin-top: 20px;
              padding: 15px;
              border: 2px solid #1890ff;
              border-radius: 8px;
              background-color: #f0f8ff;
            }
            .summary h3 {
              margin: 0 0 10px 0;
              font-size: 14px;
              color: #1890ff;
            }
            .summary-item {
              background: white;
              padding: 8px;
              border: 1px solid #d9d9d9;
              border-radius: 4px;
              text-align: center;
              margin: 5px;
              display: inline-block;
              min-width: 120px;
            }
            .summary-item strong {
              display: block;
              margin-bottom: 4px;
              color: #333;
            }
            .low-residue {
              background-color: #fff1f0 !important;
              color: #ff4d4f !important;
            }
            .normal-residue {
              background-color: #fff7e6 !important;
              color: #faad14 !important;
            }
            .high-residue {
              background-color: #f6ffed !important;
              color: #52c41a !important;
            }
            @media print {
              body { margin: 10px; }
              .header { page-break-after: avoid; }
              .summary { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ОТЧЕТ ПО МАТЕРИАЛАМ</h1>
            <div class="info">
              Дата: ${currentDate}<br>
              Количество материалов: ${selectedMaterialsData.length}
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Название</th>
                <th>Ед. измерения</th>
                <th>Остаток</th>
                <th>Средняя себестоимость USD</th>
                <th>Средняя себестоимость UZS</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              ${selectedMaterialsData.map((material, index) => {
      let statusClass = '';
      let statusText = '';

      if (material.lowerLimit !== undefined && material.upperLimit !== undefined) {
        if (material.residue < material.lowerLimit) {
          statusClass = 'low-residue';
          statusText = 'Низкий остаток';
        } else if (material.residue <= material.upperLimit) {
          statusClass = 'normal-residue';
          statusText = 'Нормальный остаток';
        } else {
          statusClass = 'high-residue';
          statusText = 'Высокий остаток';
        }
      }

      return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${material.name}</td>
                    <td>${material.unitOfMeasurement}</td>
                    <td>${roundToThreeDecimals(material.residue)}</td>
                    <td>${material.averageCost?.averageCostUSD ? roundToThreeDecimals(material.averageCost.averageCostUSD) : 'Н/Д'}</td>
                    <td>${material.averageCost?.averageCostUZS ? roundToThreeDecimals(material.averageCost.averageCostUZS) : 'Н/Д'}</td>
                    <td class="${statusClass}">${statusText}</td>
                  </tr>
                `;
    }).join('')}
            </tbody>
          </table>

          <div class="summary">
            <h3>📊 Суммарно</h3>
            <div class="summary-item">
              <strong>Общий остаток:</strong>
              <div style="font-size: 16px; color: #1890ff; font-weight: bold; margin-top: 5px;">
                ${roundToThreeDecimals(totalResidue)}
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const rowSelection = {
    selectedRowKeys: selectedMaterials,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedMaterials(selectedRowKeys as string[]);
    },
  };

  // Подсчет суммарных остатков
  const totalResidue = materials.reduce((sum, material) => sum + (material.residue || 0), 0);
  const totalResidueKg = materials.reduce((sum, material) => sum + (material.residueKg || 0), 0);

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
      render: (name: string) => <HighlightText text={name} search={debouncedSearchTerm} />,
    },
    {
      title: "Ед. измерения",
      dataIndex: "unitOfMeasurement",
      key: "unitOfMeasurement",
    },
    {
      title: "Остаток",
      dataIndex: "residue",
      key: "residue",
      render: (_: any, record: ResidueType) => {
        const { residue, lowerLimit, upperLimit } = record;

        let backgroundColor = "";
        if (lowerLimit !== undefined && upperLimit !== undefined) {
          if (residue < lowerLimit) {
            backgroundColor = "red";
          } else if (residue <= upperLimit) {
            backgroundColor = "yellow";
          } else if (residue > upperLimit) {
            backgroundColor = "red";
          }
        }

        return {
          props: {
            style: { background: backgroundColor },
          },
          children: <div>{roundToThreeDecimals(residue)}</div>,
        };
      },
    },
    {
      title: "Остаток (км)",
      dataIndex: "residueKg",
      key: "residueKg",
      render: (_: any, record: ResidueType) => {
        return <div>{roundToThreeDecimals(record.residueKg || 0)}</div>;
      },
    },
    {
      title: "Средняя себестоимость USD",
      key: "averageCostUSD",
      render: (_: any, record: ResidueType) => {
        return record.averageCost
          ? `${formatNumber(record.averageCost.averageCostUSD)}`
          : "Н/Д";
      },
    },
    {
      title: "Средняя себестоимость UZS",
      key: "averageCostUZS",
      render: (_: any, record: ResidueType) => {
        return record.averageCost
          ? `${formatNumber(record.averageCost.averageCostUZS)}`
          : "Н/Д";
      },
    },
    {
      title: "",
      key: "actions",
      render: (_: any, material: ResidueType) => {
        const menu = (
          <Menu>
            {canEditMaterials(authState.user?.role) && (
              <Menu.Item onClick={() => handleEditMaterial(material)}>
                Изменить
              </Menu.Item>
            )}
            <Menu.Item
              onClick={() =>
                navigate(
                  `/${material._id}/material/${material.name}/batches`
                )
              }
            >
              Смотреть партии
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                navigate(
                  `/${material._id}/kind/all/${material.name}/orders`
                )
              }
            >
              Смотреть заказы
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Материалы</Title>

      <Space style={{ marginBottom: 16 }}>
        <Search
          ref={searchInputRef}
          placeholder="Поиск по названию"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={handleSearch}
          allowClear
        />
        <Select
          placeholder="Фильтр по ед. измерения"
          onChange={(value) => setUnitFilter(value)}
          allowClear
          style={{ width: 200 }}
          showSearch
          filterOption={(input, option) => {
            const optionText = option?.label || option?.children;
            return String(optionText).toLowerCase().includes(input.toLowerCase());
          }}
          optionFilterProp="children"
        >
          {Object.values(UnitOfMeasurement).map((unit) => (
            <Option key={unit} value={unit}>
              {unit}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Сортировать по"
          defaultValue="name"
          onChange={(value) => setSortBy(value)}
          style={{ width: 150 }}
        >
          <Option value="name">Название</Option>
          <Option value="residue">Остаток</Option>
          <Option value="createdAt">Дата</Option>
          <Option value="totalSold">Кол-во продаж</Option>
          <Option value="totalPurchased">Кол-во покупок</Option>
        </Select>
        <Select
          defaultValue="asc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 120 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        <Checkbox
          checked={belowLimit}
          onChange={(e) => setBelowLimit(e.target.checked)}
        >
          Ниже лимита
        </Checkbox>
        <Checkbox
          checked={hasResidue}
          onChange={(e) => setHasResidue(e.target.checked)}
        >
          Имееть остаток
        </Checkbox>
        {canEditMaterials(authState.user?.role) && (
          <Button type="primary" onClick={handleAddMaterial}>
            Добавить
          </Button>
        )}
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="default"
          onClick={handlePrintMaterials}
          disabled={selectedMaterials.length === 0}
        >
          Печать ({selectedMaterials.length})
        </Button>
      </Space>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={materials}
          rowKey={(record) => record._id}
          pagination={{
            current: page,
            total: total,
            pageSize: pageSize,
            onChange: (page, size) => {
              setPage(page);
              if (size !== pageSize) setPageSize(size!);
            },
            showSizeChanger: true,
          }}
          scroll={{ x: "max-content" }}
          rowSelection={rowSelection}
        />
      )}

      <Modal
        title={selectedMaterial ? "Изменить материал" : "Добавить материал"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleFormSubmit} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="unitOfMeasurement"
            label="Ед. измерения"
            rules={[{ required: true, message: "Выберите единицу измерения" }]}
          >
            <Select
              placeholder="Выберите единицу измерения"
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {Object.values(UnitOfMeasurement).map((unit) => (
                <Option key={unit} value={unit}>
                  {unit}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="lowerLimit"
            label="Нижный лимит"
            rules={[{ required: true, message: "Введите лимит" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="upperLimit"
            label="Верхний лимит"
            rules={[{ required: true, message: "Введите лимит" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name={["price", "currency"]}
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select
              placeholder="Выберите валюту"
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.label || option?.children;
                return String(optionText).toLowerCase().includes(input.toLowerCase());
              }}
              optionFilterProp="children"
            >
              {Object.values(Currency).map((currency) => (
                <Option key={currency} value={currency}>
                  {currency}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name={["price", "amount"]}
            label="Цена"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
